/**
 * Lista de arquivos para o componente de upload, com suporte avançado a seleção, remoção, drag-and-drop, drag-out/download, pré-visualização, renomeação, acessibilidade e animações.
 *
 * **Funcionalidades:**
 * - Exibe arquivos selecionados, agrupando por pastas se houver `relativePath`.
 * - Permite seleção múltipla, remoção, cancelamento de upload e renomeação inline.
 * - Suporte a drag-and-drop para reordenação e drag-out/download (arrastar para fora para baixar/copiar).
 * - Barra de progresso individual, mensagens de erro/sucesso por arquivo.
 * - Pré-visualização de imagens.
 * - Acessibilidade: roles, aria-*, navegação por teclado, tooltips acessíveis.
 * - Animações de entrada/saída usando framer-motion.
 * - Suporte a componentes customizados (ícones, item de arquivo, pasta, barra de progresso).
 * - Estilização avançada via props de classe.
 *
 * @component
 * @example
 * <UploadFileList
 *   files={[
 *     { file: new File(["conteúdo"], "exemplo.png", { type: "image/png" }), relativePath: "imagens/exemplo.png" }
 *   ]}
 *   previewUrls={["blob:http://..."]}
 *   selectedIndexes={[0]}
 *   onToggleSelect={idx => ...}
 *   onRemove={idx => ...}
 *   onDragStart={idx => ...}
 *   onDragOver={(e, idx) => ...}
 *   onDragEnd={() => ...}
 *   RemoveIcon={MyRemoveIcon}
 *   uploadProgress={[50]}
 *   fileErrors={[null]}
 *   fileSuccess={["Enviado!"]}
 *   autoUpload
 *   onUpload
 *   abortControllers={[new AbortController()]}
 *   manualUploading
 *   theme="light"
 *   messages={{ cancel: "Cancelar", remove: "Remover" }}
 *   draggedIdx={null}
 *   onRenameFile={(idx, name) => ...}
 * />
 *
 * @typedef {Object} UploadFileObj
 * @property {File} file - O arquivo selecionado.
 * @property {string} [relativePath] - Caminho relativo (para uploads de diretórios).
 * @property {string} [customName] - Nome customizado (renomeação inline).
 *
 * @typedef {Object} UploadMessages
 * @property {string} [cancel] - Texto do botão de cancelar upload.
 * @property {string} [remove] - Texto do botão de remover arquivo.
 *
 * @typedef {Object} UploadFileListProps
 * @property {UploadFileObj[]} files - Lista de arquivos a exibir.
 * @property {(string|null)[]} previewUrls - URLs de pré-visualização (ex: imagens).
 * @property {number[]} selectedIndexes - Índices dos arquivos selecionados.
 * @property {(idx: number) => void} onToggleSelect - Alterna seleção de um arquivo.
 * @property {(idx: number) => void} onRemove - Remove um arquivo da lista.
 * @property {(fileObj: UploadFileObj, idx: number) => Promise<void>} [onDownload] - Callback para download manual (opcional).
 * @property {(fileObj: UploadFileObj, idx: number) => Promise<void>} [onDelete] - Callback para exclusão manual (opcional).
 * @property {(idx: number) => void} onDragStart - Iniciado ao arrastar para reordenar.
 * @property {(e: React.DragEvent, idx: number) => void} onDragOver - Chamado ao arrastar sobre um item.
 * @property {() => void} onDragEnd - Chamado ao finalizar drag-and-drop.
 * @property {React.ComponentType<{ className?: string }>} RemoveIcon - Ícone de remoção.
 * @property {number[]} [uploadProgress] - Progresso de upload por arquivo (0-100).
 * @property {(string|null)[]} [fileErrors] - Mensagens de erro por arquivo.
 * @property {(string|null)[]} [fileSuccess] - Mensagens de sucesso por arquivo.
 * @property {boolean} [autoUpload] - Se true, mostra barra de progresso.
 * @property {boolean} [onUpload] - Indica se há upload em andamento.
 * @property {AbortController[]} [abortControllers] - Controladores para cancelar upload.
 * @property {boolean} [manualUploading] - Se true, mostra botão de cancelar upload.
 * @property {'light'|'dark'|'white'} [theme] - Tema visual.
 * @property {UploadMessages} [messages] - Textos customizados.
 * @property {number|null} [draggedIdx] - Índice do item sendo arrastado.
 * @property {React.ComponentType<{ file: UploadFileObj; idx: number; relativePath?: string }>} [FileItemComponent] - Componente customizado para item de arquivo.
 * @property {React.ComponentType<object>} [FolderComponent] - Componente customizado para pasta.
 * @property {React.ComponentType<object>} [ProgressBarComponent] - Componente customizado para barra de progresso.
 * @property {Object} [classNames] - Classes customizadas para estilização.
 * @property {string} [classNames.fileItem] - Classe para item de arquivo.
 * @property {string} [classNames.folder] - Classe para pasta.
 * @property {string} [classNames.progressBar] - Classe para barra de progresso.
 * @property {(idx: number, newName: string) => void} [onRenameFile] - Callback para renomear arquivo.
 *
 * @param {UploadFileListProps} props
 * @returns {JSX.Element} Lista de arquivos com recursos
 */

import React from "react";
import Button from "../Button/Button";
import { FaGripVertical } from "react-icons/fa"; // Adicione esta linha
import { FixedSizeList as List } from 'react-window';
import { FaExclamationCircle } from "react-icons/fa"; // adicione ao topo
import Checkbox from "../Checkbox/Checkbox"; // ajuste o caminho se necessário
import clsx from "clsx";
import SmartImage from "../SmartImage/SmartImage"; // substitua o import do next/image
import InputField from "../InputField/InputField"; // adicione o import se necessário
import { motion, AnimatePresence } from "framer-motion"; // adicione este import

// Simple drag handle component
const DragHandle: React.FC<React.HTMLAttributes<HTMLSpanElement>> = (props) => (
  <span
    {...props}
    className={clsx(
      "cursor-grab mr-2 inline-flex items-center", // Tailwind
      props.className
    )}
    tabIndex={0}
    aria-label="Arrastar para reordenar"
    role="button"
    draggable
    onDragStart={(e: React.DragEvent<HTMLSpanElement>) => {
      if (props.onMouseDown) {
        props.onMouseDown(e as unknown as React.MouseEvent<HTMLSpanElement, MouseEvent>);
      }
    }}
  >
    <FaGripVertical aria-hidden="true" />
  </span>
);

interface UploadMessages {
  cancel?: string;
  remove?: string;
}

interface UploadFileObj {
  file: File;
  relativePath?: string;
  customName?: string;
}

interface UploadFileListProps {
  files: UploadFileObj[];
  previewUrls: (string | null)[];
  selectedIndexes: number[];
  onToggleSelect: (idx: number) => void;
  onRemove: (idx: number) => void;
  onDownload?: (fileObj: UploadFileObj, idx: number) => Promise<void>; // PATCH
  onDelete?: (fileObj: UploadFileObj, idx: number) => Promise<void>;   // PATCH
  onDragStart: (idx: number) => void;
  onDragOver: (e: React.DragEvent, idx: number) => void;
  onDragEnd: () => void;
  RemoveIcon: React.ComponentType<{ className?: string }>;
  uploadProgress?: number[];
  fileErrors?: (string | null)[];
  fileSuccess?: (string | null)[];
  autoUpload?: boolean;
  onUpload?: boolean; // <-- ALTERE AQUI: era função, agora boolean
  abortControllers?: AbortController[];
  manualUploading?: boolean;
  theme?: 'light' | 'dark' | 'white';
  messages?: UploadMessages;
  draggedIdx?: number | null; // <-- adicione esta linha
  FileItemComponent?: React.ComponentType<{ file: UploadFileObj; idx: number; relativePath?: string }>;
  FolderComponent?: React.ComponentType<object>; // <-- change here
  ProgressBarComponent?: React.ComponentType<object>; // <-- change here
  classNames?: {
    fileItem?: string;
    folder?: string;
    progressBar?: string;
  };
  onRenameFile?: (idx: number, newName: string) => void; // <-- novo prop
}

const UploadFileList: React.FC<UploadFileListProps> = (props) => {
  // Define a type for the file tree node
  interface FileTreeNode {
    files?: { file: File; idx: number; relativePath?: string }[];
    folders?: Record<string, FileTreeNode>;
  }

  // Agrupa arquivos por pasta
  const buildFileTree = (files: UploadFileListProps["files"]) => {
    const tree: FileTreeNode = {};
    files.forEach((fileObj, idx) => {
      const parts = (fileObj.relativePath || fileObj.file.name).split("/");
      let node: FileTreeNode = tree;
      parts.forEach((part, i) => {
        if (i === parts.length - 1) {
          if (!node.files) node.files = [];
          node.files.push({ file: fileObj.file, idx, relativePath: fileObj.relativePath });
        } else {
          if (!node.folders) node.folders = {};
          node.folders[part] = node.folders[part] || {};
          node = node.folders[part];
        }
      });
    });
    return tree;
  };

  // Renderiza a árvore de arquivos e pastas
  const renderNode = (
    node: FileTreeNode,
    path: string[] = []
  ): React.ReactNode => {
    const folders = node.folders ? Object.keys(node.folders) : [];
    return (
      <AnimatePresence>
        <ul>
          {folders.map((folder) => (
            <motion.li
              key={folder}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <strong>{folder}/</strong>
              {node.folders && renderNode(node.folders[folder], [...path, folder])}
            </motion.li>
          ))}
          {node.files &&
            node.files.map((fileObj) => {
              const idx = fileObj.idx;
              const file = fileObj.file;
              return (
                <motion.li
                  key={file.name + idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                  data-testid={`upload-file-item-${idx}`} // <-- adicione esta linha
                  className={clsx(
                    "flex items-center justify-between bg-[#f8f8f8] rounded-md px-2 py-1 mb-1 text-[0.97em] cursor-move select-none transition-colors duration-200",
                    props.selectedIndexes.includes(idx) && "bg-[#e3f2fd]",
                    props.draggedIdx === idx && "opacity-70",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#009EEB] focus-visible:outline-offset-2"
                  )}
                  // draggable
                  // onDragStart={() => onDragStart(idx)}
                  onDragOver={(e) => props.onDragOver(e, idx)}
                  onDragEnd={props.onDragEnd}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onToggleSelect(idx);
                  }}
                  tabIndex={0}
                  aria-label={`Arquivo: ${file.name}, tamanho ${(file.size / 1024).toFixed(1)} KB`}
                  role="treeitem"
                  aria-selected={props.selectedIndexes.includes(idx)}
                >
                  {/* Drag handle */}
                  <DragHandle onMouseDown={(e) => { e.stopPropagation(); props.onDragStart(idx); }} />
                  <Checkbox
                    checked={props.selectedIndexes.includes(idx)}
                    onChange={() => props.onToggleSelect(idx)}
                    label=""
                    tabIndex={0}
                    theme={props.theme === "dark" ? "dark" : "light"}
                    containerProps={{
                      onClick: (e: React.MouseEvent) => e.stopPropagation(),
                      className: "mr-2", // Tailwind
                    }}
                    accessibilityLabel={`Selecionar arquivo ${file.name}`}
                  />
                  {file.type.startsWith('image/') && props.previewUrls[idx] && (
                    <SmartImage
                      src={props.previewUrls[idx]!}
                      alt={file.name}
                      width={32}
                      height={32}
                      className="object-cover mr-1 rounded"
                      layout="fixed"
                      quality={70}
                      placeholder="empty"
                      ariaHidden={false}
                    />
                  )}
                  <span className="flex-1 flex items-center gap-2" id={`upload-file-info-${idx}`}>
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    {props.autoUpload && props.onUpload && (
                      <>
                        <span
                          className="ml-2"
                          data-testid={`upload-progress-${idx}`}
                          id={`upload-progress-label-${idx}`}
                          aria-live="polite"
                        >
                          {props.uploadProgress && props.uploadProgress[idx] != null ? ` – ${props.uploadProgress[idx]}%` : ''}
                        </span>
                        <div className="relative bg-[#e0e0e0] rounded h-[5px] mt-0.5 w-[100px] inline-block align-middle ml-2">
                          <div
                            className="bg-[#009EEB] h-full rounded transition-all duration-500"
                            style={{ width: `${(props.uploadProgress?.[idx] ?? 0)}%` }}
                            role="progressbar"
                            aria-valuenow={props.uploadProgress?.[idx] ?? 0}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Progresso do upload de ${file.name}`}
                            aria-describedby={`upload-progress-label-${idx}`}
                            tabIndex={0}
                          />
                        </div>
                      </>
                    )}
                    {props.fileErrors && props.fileErrors[idx] && (
                      <span
                        className="text-red-600 ml-2 relative inline-block"
                        id={`upload-file-error-${idx}`}
                        role="alert"
                        aria-live="assertive"
                        tabIndex={0}
                      >
                        <span
                          className="cursor-pointer align-middle"
                          tabIndex={0}
                          aria-label={props.fileErrors[idx] || "Erro"}
                          onFocus={() => setTooltipOpen(idx)}
                          onBlur={() => setTooltipOpen(null)}
                          onMouseEnter={() => setTooltipOpen(idx)}
                          onMouseLeave={() => setTooltipOpen(null)}
                        >
                          <FaExclamationCircle aria-hidden="true" />
                          {tooltipOpen === idx && (
                            <span
                              className="absolute left-6 -top-2 z-20 bg-white text-red-700 border border-red-700 rounded px-2 py-1 text-[13px] min-w-[120px] shadow-lg"
                              role="tooltip"
                            >
                              {props.fileErrors[idx]}
                            </span>
                          )}
                        </span>
                      </span>
                    )}
                    {props.fileSuccess && props.fileSuccess[idx] && (
                      <span
                        className="text-green-600 ml-2"
                        id={`upload-file-success-${idx}`}
                        role="status"
                        aria-live="polite"
                      >
                        {props.fileSuccess[idx]}
                      </span>
                    )}
                  </span>
                  {props.manualUploading && props.abortControllers && props.abortControllers[idx] && (
                    <Button
                      title={props.messages?.cancel}
                      ariaLabel={props.messages?.cancel}
                      onClick={(e) => {
                        e?.stopPropagation?.();
                        props.abortControllers && props.abortControllers[idx]?.abort();
                      }}
                      type="button"
                      size="tiny"
                      theme={props.theme === "white" ? "light" : props.theme}
                      buttonClassName="ml-2"
                      data-testid={`upload-cancel-btn-${idx}`}
                      tabIndex={0}
                    >
                      {props.messages?.cancel}
                    </Button>
                  )}
                  <Button
                    title={props.messages?.remove}
                    ariaLabel={props.messages?.remove}
                    aria-describedby={`upload-file-info-${idx}`}
                    onClick={(e) => {
                      e?.stopPropagation?.();
                      props.onRemove(idx);
                    }}
                    type="button"
                    size="tiny"
                    theme={props.theme === "white" ? "light" : props.theme}
                    buttonClassName="ml-2"
                    tabIndex={0}
                  >
                    <props.RemoveIcon aria-hidden="true" />{props.messages?.remove}
                  </Button>
                </motion.li>
              );
            })}
        </ul>
      </AnimatePresence>
    );
  };

  // Lista plana (sem subpastas)
  const [focusIndex, setFocusIndex] = React.useState<number | null>(null);
  const [tooltipOpen, setTooltipOpen] = React.useState<number | null>(null);
  const [editingIdx, setEditingIdx] = React.useState<number | null>(null);
  const [editValue, setEditValue] = React.useState<string>("");

  // Se houver relativePath com subpastas, exiba árvore
  const hasFolders = props.files.some(f => f.relativePath && f.relativePath.includes("/"));
  if (hasFolders) {
    const tree = buildFileTree(props.files);
    return (
      <div className="upload-file-tree" tabIndex={0} aria-label="Estrutura de pastas" role="tree">
        {renderNode(tree)}
      </div>
    );
  }

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const fileObj = props.files[index];
    const file = fileObj.file;
    const isSelected = props.selectedIndexes.includes(index);
    const displayName = fileObj.customName || file.name;
    const liRef = React.useRef<HTMLLIElement>(null);

    // Handler para drag-out/download
    const handleNativeDragStart = React.useCallback((e: DragEvent) => {
      // Cria um objeto de download temporário
      const url = URL.createObjectURL(file);
      e.dataTransfer?.setData(
        "DownloadURL",
        `${file.type || "application/octet-stream"}:${file.name}:${url}`
      );
      e.dataTransfer?.setData("application/octet-stream", url);
      e.currentTarget?.addEventListener(
        "dragend",
        () => {
          URL.revokeObjectURL(url);
        },
        { once: true }
      );
      if (props.onDragStart) props.onDragStart(index);
    }, [file, index]);

    // Adiciona/remover o listener nativo
    React.useEffect(() => {
      const node = liRef.current;
      if (node) {
        node.addEventListener("dragstart", handleNativeDragStart as EventListener);
        return () => node.removeEventListener("dragstart", handleNativeDragStart as EventListener);
      }
    }, [handleNativeDragStart]);

    return (
      <motion.li
        ref={liRef}
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.25 }}
        style={style}
        key={file.name + index}
        className={`upload-file-item${isSelected ? ' selected' : ''}${focusIndex === index ? ' focus-visible' : ''}`}
        onDragOver={(e) => props.onDragOver(e, index)}
        onDragEnd={props.onDragEnd}
        onClick={(e) => {
          e.stopPropagation();
          props.onToggleSelect(index);
        }}
        tabIndex={0}
        aria-label={`Arquivo: ${file.name}, tamanho ${(file.size / 1024).toFixed(1)} KB`}
        role="option"
        aria-selected={isSelected}
        onFocus={() => setFocusIndex(index)}
        onBlur={() => setFocusIndex(null)}
        draggable
        // Remova o onDragStart do motion.li!
      >
        {/* Drag handle */}
        <DragHandle onMouseDown={(e) => { e.stopPropagation(); props.onDragStart(index); }} />

        <Checkbox
          checked={isSelected}
          onChange={() => props.onToggleSelect(index)}
          label=""
          tabIndex={0}
          theme={props.theme === "dark" ? "dark" : "light"}
          containerProps={{
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            className: "mr-2",
          }}
          accessibilityLabel={`Selecionar arquivo ${file.name}`}
        />
        {file.type.startsWith('image/') && props.previewUrls[index] && (
          <SmartImage
            src={props.previewUrls[index]!}
            alt={file.name}
            width={32}
            height={32}
            className="object-cover mr-1 rounded"
            layout="fixed"
            quality={70}
            placeholder="empty"
            ariaHidden={false}
          />
        )}
        <span className="flex-1 flex items-center gap-2" id={`upload-file-info-${index}`}>
          {editingIdx === index ? (
            <InputField
              type="text"
              value={editValue}
              onChange={(value) => setEditValue(value as string)}
              autoFocus
              inputClassName="border rounded px-1 py-0.5 w-40"
              onBlur={() => {
                props.onRenameFile?.(index, editValue.trim() || file.name);
                setEditingIdx(null);
              }}
              inputProps={{
                style: { fontSize: "1em" },
                tabIndex: 0,
                "aria-label": `Editar nome do arquivo ${displayName}`,
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  if (e.key === "Enter") {
                    props.onRenameFile?.(index, editValue.trim() || file.name);
                    setEditingIdx(null);
                  }
                  if (e.key === "Escape") {
                    setEditingIdx(null);
                  }
                }
              }}
            />
          ) : (
            <span
              onDoubleClick={() => {
                setEditingIdx(index);
                setEditValue(displayName);
              }}
              style={{ cursor: "pointer" }}
              title="Clique duas vezes para renomear"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === "F2") {
                  setEditingIdx(index);
                  setEditValue(displayName);
                }
              }}
            >
              {displayName} ({(file.size / 1024).toFixed(1)} KB)
            </span>
          )}
          {props.autoUpload && props.onUpload && (
            <>
              <span
                className="ml-2"
                data-testid={`upload-progress-${index}`}
                id={`upload-progress-label-${index}`}
                aria-live="polite"
              >
                {props.uploadProgress && props.uploadProgress[index] != null ? ` – ${props.uploadProgress[index]}%` : ''}
              </span>
              <div className="relative bg-[#e0e0e0] rounded h-[5px] mt-0.5 w-[100px] inline-block align-middle ml-2">
                <div
                  className="bg-[#009EEB] h-full rounded transition-all duration-500"
                  style={{ width: `${props.uploadProgress?.[index] ?? 0}%` }}
                  role="progressbar"
                  aria-valuenow={props.uploadProgress?.[index] ?? 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Progresso do upload de ${file.name}`}
                  aria-describedby={`upload-progress-label-${index}`}
                  tabIndex={0}
                />
              </div>
            </>
          )}
          {props.fileErrors && props.fileErrors[index] && (
            <span
              className="text-red-600 ml-2 relative inline-block"
              id={`upload-file-error-${index}`}
              role="alert"
              aria-live="assertive"
              tabIndex={0}
            >
              <span
                className="cursor-pointer align-middle"
                tabIndex={0}
                aria-label={props.fileErrors[index] || "Erro"}
                style={{ cursor: "pointer", verticalAlign: "middle" }}
                onFocus={() => setTooltipOpen(index)}
                onBlur={() => setTooltipOpen(null)}
                onMouseEnter={() => setTooltipOpen(index)}
                onMouseLeave={() => setTooltipOpen(null)}
              >
                <FaExclamationCircle aria-hidden="true" />
                {tooltipOpen === index && (
                  <span className="upload-tooltip upload-file-error-tooltip" role="tooltip" style={{
                    position: "absolute",
                    left: 24,
                    top: -8,
                    zIndex: 20,
                    background: "#fff",
                    color: "#c00",
                    border: "1px solid #c00",
                    borderRadius: 4,
                    padding: "6px 10px",
                    fontSize: 13,
                    minWidth: 120,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                  }}>
                    {props.fileErrors[index]}
                  </span>
                )}
              </span>
            </span>
          )}
          {props.fileSuccess && props.fileSuccess[index] && (
            <span
              className="text-green-600 ml-2"
              id={`upload-file-success-${index}`}
              role="status"
              aria-live="polite"
            >
              {props.fileSuccess[index]}
            </span>
          )}
        </span>
        {props.manualUploading && props.abortControllers && props.abortControllers[index] && (
          <Button
            title={props.messages?.cancel}
            ariaLabel={props.messages?.cancel}
            onClick={(e) => {
              e?.stopPropagation?.();
              props.abortControllers?.[index]?.abort();
            }}
            type="button"
            size="tiny"
            theme={props.theme === "white" ? "light" : props.theme}
            buttonClassName="ml-2"
            data-testid={`upload-cancel-btn-${index}`}
            tabIndex={0}
          >
            {props.messages?.cancel}
          </Button>
        )}
        <Button
          title={props.messages?.remove}
          ariaLabel={props.messages?.remove}
          aria-describedby={`upload-file-info-${index}`}
          onClick={(e) => {
            e?.stopPropagation?.();
            props.onRemove(index);
          }}
          type="button"
          size="tiny"
          theme={props.theme === "white" ? "light" : props.theme}
          buttonClassName="ml-2"
          tabIndex={0}
        >
          <props.RemoveIcon aria-hidden="true" />{props.messages?.remove}
        </Button>
      </motion.li>
    );
  };

  // Use componentes customizados se fornecidos
  // (ProgressBarComponent is not used directly here)

  // Lista plana (sem subpastas)
  // (focusIndex and tooltipOpen already declared above, so do not redeclare here)

  return (
    <ul
      role="listbox"
      aria-label="Lista de arquivos"
      aria-multiselectable="true"
      tabIndex={0}
      style={{ outline: 'none' }}
    >
      <AnimatePresence initial={false}>
        <List
          height={240}
          itemCount={props.files.length}
          itemSize={56}
          width="100%"
        >
          {Row}
        </List>
      </AnimatePresence>
    </ul>
  );
};

export default UploadFileList;